/**
 * InstagramPostBanner — compact Instagram post reference bar with modal dialog
 * for full post preview (caption, image, permalink, and post details).
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { ExternalLink, Eye, Image as ImageIcon } from 'lucide-react'
import { InstagramIcon } from '../../modules/conversations/BrandIcons.js'
import { cleanIdentifier, isNumericOrRawId } from '../../utils/formatCustomerName.js'

export interface InstagramPostBannerProps {
  /** Instagram media / post ID. */
  postId?: string | null
  /** Post caption / text. */
  postMessage?: string | null
  /** Post image URL. */
  postPicture?: string | null
  /** Direct link to the Instagram post. */
  permalink?: string | null
  /** Compact variant for dense message threads. */
  compact?: boolean
  /** Instagram account name or handle. */
  accountHolderName?: string | null
  /** Account ID or author ID. */
  accountId?: string | null
  /** Custom button label (defaults to 'View') */
  buttonLabel?: string
}

/** Fresh image first (the API re-resolves Instagram's expiring CDN link from
 *  the media id), then whatever URL was captured when the comment was polled. */
function imageCandidates(postId: string, picture: string): string[] {
  const candidates: string[] = []
  if (/^\d+$/.test(postId)) candidates.push(`/api/agent/instagram-media/${encodeURIComponent(postId)}/image`)
  if (picture && !candidates.includes(picture)) candidates.push(picture)
  return candidates
}

export function InstagramPostBanner({
  postId,
  postMessage,
  postPicture,
  permalink,
  compact = false,
  accountHolderName,
  accountId,
  buttonLabel = 'View',
}: InstagramPostBannerProps): JSX.Element | null {
  const [dialogVisible, setDialogVisible] = useState(false)

  const message = postMessage?.trim() ?? ''
  const picture = postPicture?.trim() ?? ''
  const link = permalink?.trim() ?? ''
  const id = postId?.trim() ?? ''
  const accId = accountId?.trim() ?? ''

  // Format account holder name cleanly
  const rawAccountName = accountHolderName?.trim() ?? ''
  const displayAccountName = useMemo(() => {
    const cleaned = cleanIdentifier(rawAccountName)
    if (cleaned && !isNumericOrRawId(cleaned)) {
      return cleaned
    }
    return 'Instagram Post'
  }, [rawAccountName])

  // Display ID for reference
  const displayId = id || accId || ''

  const candidates = useMemo(() => imageCandidates(id, picture), [id, picture])
  const [candidateIndex, setCandidateIndex] = useState(0)

  useEffect(() => {
    setCandidateIndex(0)
  }, [candidates])

  const imageSrc = candidateIndex >= 0 ? candidates[candidateIndex] : undefined
  const imageError = candidates.length > 0 && !imageSrc
  const handleImageError = (): void => {
    setCandidateIndex((current) => (current >= 0 && current + 1 < candidates.length ? current + 1 : -1))
  }

  // If there's neither a post ID, caption, picture, nor link, nothing to render.
  if (!message && !picture && !link && !id) {
    return null
  }

  return (
    <>
      {/* Compact Instagram Post Reference Bar */}
      <section
        data-testid="instagram-post-banner"
        className={[
          'flex min-w-0 items-center justify-between gap-3 border border-[#E4405F]/20 bg-[#E4405F]/5 transition-colors',
          compact
            ? 'mb-2.5 rounded-[3px] px-2.5 py-1.5'
            : 'shrink-0 rounded-[3px] px-3.5 py-2',
        ].join(' ')}
        aria-label="Instagram post reference"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[3px] bg-[#E4405F] text-white shadow-xs"
            aria-hidden="true"
          >
            <InstagramIcon className="h-3 w-3" />
          </span>

          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs font-semibold text-slate-800">
              {displayAccountName}
            </span>

            {displayId && (
              <>
                <span className="text-slate-300" aria-hidden="true">·</span>
                <span
                  className="truncate font-mono text-[11px] text-slate-500"
                  title={displayId}
                >
                  ID: {displayId}
                </span>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setDialogVisible(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-[3px] bg-[#E4405F] px-2.5 py-1 text-xs font-medium text-white shadow-xs transition-colors hover:bg-[#d62976] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E4405F]"
          aria-label="View Instagram Post"
        >
          <Eye className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{buttonLabel}</span>
        </button>
      </section>

      {/* Full Instagram Post Modal Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-2">
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[3px] bg-[#E4405F] text-white"
              aria-hidden="true"
            >
              <InstagramIcon className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <span className="text-sm font-bold text-slate-900">
                Instagram Post
              </span>
              {displayAccountName !== 'Instagram Post' && (
                <span className="ml-1.5 text-xs font-normal text-slate-500">
                  by {displayAccountName}
                </span>
              )}
            </div>
          </div>
        }
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '560px', maxWidth: '95vw' }}
        modal
        dismissableMask
        className="p-fluid"
      >
        <div className="max-h-[75vh] space-y-4 overflow-y-auto pr-1">
          {/* Post metadata header */}
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <div className="min-w-0 space-y-0.5">
              <p className="font-semibold text-slate-800">
                Account: <span className="font-medium text-slate-700">{displayAccountName}</span>
              </p>
              {displayId && (
                <p className="truncate font-mono text-[11px] text-slate-500" title={displayId}>
                  Post ID: {displayId}
                </p>
              )}
            </div>

            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1 rounded-[3px] bg-white border border-slate-200 px-2.5 py-1 text-xs font-medium text-[#E4405F] transition-colors hover:bg-pink-50 hover:text-pink-800"
              >
                <span>Open on Instagram</span>
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </div>

          {/* Post image */}
          {imageSrc && (
            <div className="flex justify-center overflow-hidden rounded-[3px] border border-slate-200 bg-slate-900/5 p-2">
              <img
                src={imageSrc}
                alt="Instagram post attachment"
                loading="lazy"
                className="block max-h-[380px] w-auto max-w-full rounded-[3px] object-contain shadow-xs"
                onError={handleImageError}
              />
            </div>
          )}

          {/* Image fallback if loading failed */}
          {imageError && (
            <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
              <ImageIcon className="h-4 w-4 shrink-0 text-slate-400" />
              <span>Post media could not be loaded or has expired.</span>
            </div>
          )}

          {/* Post caption / text */}
          {message ? (
            <div className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Caption
              </span>
              <div className="max-h-64 overflow-y-auto rounded-[3px] border border-slate-200 bg-slate-50 p-3.5 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap break-words">
                {message}
              </div>
            </div>
          ) : candidates.length === 0 ? (
            <div className="rounded-[3px] border border-slate-100 bg-slate-50 p-3 text-xs italic text-slate-500">
              This post has no caption.
            </div>
          ) : (
            <div className="rounded-[3px] border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Photo / Reel post without text caption.
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-[3px] border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <span>View Original Post</span>
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
            </a>
          )}
          <button
            type="button"
            onClick={() => setDialogVisible(false)}
            className="rounded-[3px] bg-slate-100 px-3.5 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline-none"
          >
            Close
          </button>
        </div>
      </Dialog>
    </>
  )
}

export default InstagramPostBanner
